/**
 * @file xeroReports.js
 * @version 1.3.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-15
 * @modified 2026-10-23
 * @description Reports → Xero Reports, the screen's own small rules: a report's
 *              filters from the server's defaults, the query string they make
 *              (the same for the report and its Excel export), which header
 *              filters differ from the export's, how a cell reads, the
 *              dropdowns' options and the period presets. The reports
 *              themselves — what they select, signs, totals, the price check,
 *              the column filters and the pages — are the server's
 *              (utils/xeroReports.js); nothing here works a figure out.
 *
 * 1.2.0 (15 Oct 2026, Cam's list): several contacts (contactIds) and items
 * (itemCodes) chosen from searchable lists instead of typed; the column filters
 * and the page travel with the query; AHDB Levy's "Only self-billed"; the
 * period presets come from the server, worked out for today (London) with the
 * financial year Xero holds. Paging moved to the server (by lines, totals over
 * everything), so pageOfGroups is gone.
 *
 * 1.3.0 (23 Oct 2026, Cam): Despatch's "Only without supplied qty"
 * (unsuppliedOnly) travels with the query and counts as a header filter.
 */

import { formatCurrency, formatPrice } from './currencyFormatter';
import { formatQuantity, formatTonnes } from './numberFormatters';
import { formatDateShort } from './dateFormatters';

/** Lines a page shows (the server's PAGE_SIZE; as the search screens). */
export const PAGE_SIZE = 50;

/** The value the column filters use for an empty cell. */
export const BLANK = '(blank)';

/** The filters a report opens with, from the server's defaults. */
export function filtersFromDefaults(defaults) {
  const d = defaults || {};
  return {
    preset: d.period?.preset || 'custom',
    from: d.period?.from || '',
    to: d.period?.to || '',
    dueFrom: d.due?.from || '',
    dueTo: d.due?.to || '',
    statuses: [...(d.statuses || [])],
    contactIds: (d.contactIds || []).map(String),
    itemCodes: [...(d.itemCodes || [])],
    includeHaulage: !!d.includeHaulage,
    includeVoided: !!d.includeVoided,
    onlyFlagged: !!d.onlyFlagged,
    selfBilledOnly: !!d.selfBilledOnly,
    unsuppliedOnly: !!d.unsuppliedOnly
  };
}

const uniq = (list) => [...new Set((list || []).map(v => String(v).trim()).filter(Boolean))];

/**
 * The query string for a report (and its export) from the filters on screen.
 * `columnFilters` is the header filters' JSON (useColumnFilters.queryValue);
 * `page` / `limit` only for the screen — the export is always the whole result.
 */
export function reportQuery(def, f, { columnFilters = '', page = null, limit = null } = {}) {
  const q = new URLSearchParams();
  q.set('from', f.from || '');
  q.set('to', f.to || '');
  if (def.family === 'po') {
    q.set('dueFrom', f.dueFrom || '');
    q.set('dueTo', f.dueTo || '');
  }
  q.set('statuses', (f.statuses || []).join(','));
  const contacts = uniq(f.contactIds);
  if (contacts.length) q.set('contactIds', contacts.join(','));
  if (def.filters.includes('itemCodes') || def.filters.includes('stockRange')) {
    q.set('itemCodes', uniq(f.itemCodes).join(','));
  }
  if (def.filters.includes('haulage')) q.set('includeHaulage', f.includeHaulage ? 'true' : 'false');
  if (def.filters.includes('includeVoided')) q.set('includeVoided', f.includeVoided ? 'true' : 'false');
  if (def.filters.includes('onlyFlagged')) q.set('onlyFlagged', f.onlyFlagged ? 'true' : 'false');
  if (def.filters.includes('selfBilledOnly')) q.set('selfBilledOnly', f.selfBilledOnly ? 'true' : 'false');
  if (def.filters.includes('unsuppliedOnly')) q.set('unsuppliedOnly', f.unsuppliedOnly ? 'true' : 'false');
  if (columnFilters) q.set('columnFilters', columnFilters);
  if (page != null) q.set('page', String(page));
  if (limit != null) q.set('limit', String(limit));
  return q.toString();
}

// Fixed key order and booleans, so filters kept from before a field existed
// still compare equal to the defaults; lists compared as sets.
const norm = (x) => ({
  period: `${x.from || ''}|${x.to || ''}`,
  due: `${x.dueFrom || ''}|${x.dueTo || ''}`,
  statuses: [...(x.statuses || [])].sort().join(','),
  contacts: uniq(x.contactIds).sort().join(','),
  items: uniq(x.itemCodes).map(c => c.toUpperCase()).sort().join(','),
  includeHaulage: !!x.includeHaulage,
  includeVoided: !!x.includeVoided,
  onlyFlagged: !!x.onlyFlagged,
  selfBilledOnly: !!x.selfBilledOnly,
  unsuppliedOnly: !!x.unsuppliedOnly
});

/**
 * The header filters that differ from the report's export defaults, named for
 * the ColumnFilterNote pill ({ count, labels }, as utils/activeFilters). The
 * preset's NAME never counts — only the dates it set.
 */
export function headerFiltersChanged(f, def) {
  const a = norm(f || {});
  const b = norm(filtersFromDefaults(def?.defaults));
  const po = def?.family === 'po';
  const names = {
    period: 'Period',
    due: 'Due date',
    statuses: 'Status',
    contacts: po ? 'Supplier' : 'Customer',
    items: def?.key === 'stock-pricing' ? 'Stock range' : 'Items',
    includeHaulage: 'Haulage lines',
    includeVoided: 'Voided and deleted',
    onlyFlagged: 'Only lines to check',
    selfBilledOnly: 'Only self-billed',
    unsuppliedOnly: 'Only without supplied qty'
  };
  const labels = Object.keys(names).filter(k => a[k] !== b[k]).map(k => names[k]);
  return { count: labels.length, labels };
}

/** Do the filters differ from the report's defaults? (Drives Clear Filters.) */
export function filtersChanged(f, defaults) {
  return JSON.stringify(norm(f || {})) !== JSON.stringify(norm(filtersFromDefaults(defaults)));
}

/** How one cell reads, by its column's type (the system's one formatter each). */
export function cellText(col, value) {
  if (value === null || value === undefined || value === '') return '';
  switch (col?.type) {
    case 'date': return formatDateShort(value);
    case 'qty': return formatQuantity(value);
    case 'tonnes': return formatTonnes(value);
    case 'price': return formatPrice(value);
    case 'money': return formatCurrency(value);
    default: return String(value);
  }
}

/** The words a column-filter value is shown in (a date as 03/09/2026, £ for money). */
export function columnOptionLabel(columns, key, value) {
  if (value === BLANK) return BLANK;
  const col = (columns || []).find(c => c.key === key);
  return col ? (cellText(col, value) || value) : value;
}

/** The numeric column types: right-aligned, never wrapped. */
export const isNumeric = (col) => ['qty', 'price', 'money', 'tonnes'].includes(col?.type);

/**
 * The contacts dropdown's options: account number first in the search text,
 * archived ones said. `{ value, name, accountNo, archived, searchText }`.
 */
export function contactOptions(contacts = []) {
  return contacts.map(c => ({
    value: String(c.id),
    name: c.name || '',
    accountNo: c.accountNo || '',
    archived: !!c.archived,
    searchText: `${c.accountNo || ''} ${c.name || ''}${c.archived ? ' archived' : ''}`
  }));
}

/**
 * The items dropdown's options: every item (code + name), plus any code the
 * filters hold that is not an item (Xero's export filtered on codes such as
 * "Whole Beans", and an item may since have been deleted) so a chosen code is
 * never invisible. Chosen codes first, so a saved range reads at a glance.
 */
export function itemOptions(items = [], chosen = []) {
  const byCode = new Map();
  for (const i of items) {
    const code = String(i.code || '').trim();
    if (code && !byCode.has(code.toUpperCase())) {
      byCode.set(code.toUpperCase(), { value: code, code, name: i.name || '', archived: !!i.archived });
    }
  }
  const picked = [];
  for (const c of uniq(chosen)) {
    const known = byCode.get(c.toUpperCase());
    if (known) { picked.push({ ...known, value: c }); byCode.delete(c.toUpperCase()); }
    else picked.push({ value: c, code: c, name: '', archived: false, unknown: true });
  }
  return [...picked, ...byCode.values()].map(o => ({
    ...o,
    searchText: `${o.code} ${o.name}${o.archived ? ' archived' : ''}`
  }));
}

/** The dates a preset means, from the server's list (worked out for today). */
export function presetRange(presets = [], value) {
  const p = presets.find(x => x.value === value);
  return p && p.from && p.to ? { from: p.from, to: p.to } : null;
}

/** "Lines 51–100 of 4,516" — the page's place in the whole result. */
export function pageLines(pagination) {
  if (!pagination || !pagination.total) return '';
  const first = (pagination.page - 1) * pagination.limit + 1;
  const last = Math.min(pagination.page * pagination.limit, pagination.total);
  return `Lines ${first.toLocaleString('en-GB')}–${last.toLocaleString('en-GB')} of ${pagination.total.toLocaleString('en-GB')}`;
}

/** "Accrual - 2026-04-01 to 2026-10-31.xlsx" from the answer's header, else a fallback. */
export function fileNameFrom(res, fallback) {
  const cd = res?.headers?.get?.('content-disposition') || '';
  const m = /filename="?([^";]+)"?/i.exec(cd);
  return m ? m[1] : fallback;
}
